import { X } from 'lucide-react'
import { useState } from 'react'

import { CurrencySelector } from '@/components/common'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateAccount,
  useUpdateAccount,
  type AccountWithBalance,
  type AccountType,
  type CreateAccountInput,
} from '@/hooks'

interface AccountFormProps {
  account?: AccountWithBalance
  onClose: () => void
  onSuccess: () => void
}

const accountTypes: { value: AccountType; label: string }[] = [
  { value: 'cash', label: '现金' },
  { value: 'bank', label: '银行' },
  { value: 'digital', label: '数字账户' },
]



export function AccountForm({ account, onClose, onSuccess }: AccountFormProps) {
  const isEditing = !!account
  const [formData, setFormData] = useState<CreateAccountInput>({
    name: account?.name ?? '',
    type: account?.type ?? 'bank',
    currency: account?.currency ?? 'AED',
    initialBalance: account?.initialBalance ?? 0,
  })
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateAccount()
  const updateMutation = useUpdateAccount()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const submitAsync = async () => {
      try {
        if (isEditing && account) {
          await updateMutation.mutateAsync({
            id: account.id,
            data: {
              name: formData.name,
              type: formData.type,
              currency: formData.currency,
            },
          })
        } else {
          await createMutation.mutateAsync({
            ...formData,
            initialBalance: Math.round((formData.initialBalance ?? 0) * 100),
          })
        }
        onSuccess()
      } catch (err) {
        setError(err instanceof Error ? err.message : '操作失败')
      }
    }

    void submitAsync()
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEditing ? '编辑账户' : '新建账户'}</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">账户名称</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="请输入账户名称"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">账户类型</label>
            <Select
              value={formData.type}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, type: value as AccountType }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="选择账户类型" />
              </SelectTrigger>
              <SelectContent>
                {accountTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">币种</label>
            <CurrencySelector
              value={formData.currency ?? 'AED'}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, currency: value }))}
              className="w-full"
            />
          </div>

          {!isEditing && (
            <div>
              <label className="mb-1 block text-sm font-medium">初始余额</label>
              <input
                type="number"
                step="0.01"
                value={(formData.initialBalance ?? 0) / 100}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    initialBalance: Math.round(parseFloat(e.target.value || '0') * 100),
                  }))
                }
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="0.00"
              />
            </div>
          )}

          {error && <div className="text-sm text-destructive">{error}</div>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
